import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { taxRulesApi } from '@/api';
import { useMasterData } from '@/hooks/useMasterData';
import { ProductCatalogNav } from '@/components/org/ProductCatalogNav';
import { CountrySelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { TaxRuleRequest, TaxRuleResponse } from '@/types/taxRule';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const empty: TaxRuleRequest = {
  countryCode: 'IN',
  taxName: '',
  taxPercentage: 0,
  taxType: 'PERCENTAGE',
};

export function TaxRulesPage() {
  const { taxModes, loading: metaLoading } = useMasterData();
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<TaxRuleResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TaxRuleResponse | null>(null);
  const [form, setForm] = useState<TaxRuleRequest>(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await taxRulesApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load tax rules');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<TaxRuleResponse>[] = [
    { key: 'taxName', header: 'Tax name' },
    { key: 'countryCode', header: 'Country' },
    { key: 'stateCode', header: 'State' },
    { key: 'taxPercentage', header: 'Rate %' },
    { key: 'taxMode', header: 'Mode' },
    {
      key: 'edit',
      header: '',
      render: (r) => (
        <button
          type="button"
          className="text-primary-600"
          onClick={() => {
            setEditing(r);
            setForm({
              countryCode: r.countryCode,
              stateCode: r.stateCode,
              taxName: r.taxName,
              taxPercentage: r.taxPercentage,
              taxType: r.taxType,
              taxMode: r.taxMode,
              effectiveFrom: r.effectiveFrom,
              effectiveTo: r.effectiveTo,
            });
            setModalOpen(true);
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await taxRulesApi.update(editing.id, form);
      else await taxRulesApi.create(form);
      notify.success('Saved');
      setModalOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!confirm('Delete this tax rule?')) return;
    try {
      await taxRulesApi.delete(id);
      notify.success('Deleted');
      load();
    } catch (err) {
      showApiError(err, 'Delete failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Tax Rules"
        description="Country/state tax rules used by stores at checkout"
        breadcrumbs={[{ label: 'POS', to: '/pos/dashboard' }, { label: 'Tax Rules' }]}
        actions={
          <Button onClick={() => { setEditing(null); setForm(empty); setModalOpen(true); }}>
            <AddIcon fontSize="small" /> Add Rule
          </Button>
        }
      />
      <ProductCatalogNav />
      <DataTable columns={[
          ...columns,
          {
            key: 'delete',
            header: '',
            render: (r) => (
              <button type="button" className="text-red-600" onClick={() => remove(r.id)}>
                Delete
              </button>
            ),
          },
        ]}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => { pagination.setSearch(v); pagination.resetPage(); }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
      />
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Tax Rule' : 'New Tax Rule'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} loading={saving}>Save</Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Tax name *" value={form.taxName} onChange={(e) => setForm({ ...form, taxName: e.target.value })} />
          <Input label="Rate % *" type="number" value={String(form.taxPercentage)} onChange={(e) => setForm({ ...form, taxPercentage: Number(e.target.value) })} />
          <CountrySelect
            label="Country *"
            disabled={metaLoading}
            required
            value={form.countryCode}
            onChange={(v) => setForm({ ...form, countryCode: v })}
          />
          <Input label="State code" value={form.stateCode ?? ''} onChange={(e) => setForm({ ...form, stateCode: e.target.value })} />
          <Select
            label="Tax mode"
            disabled={metaLoading}
            placeholder="Select tax mode"
            options={taxModes.map((t) => ({ value: t.code, label: t.label }))}
            value={form.taxMode ?? ''}
            onChange={(v) => setForm({ ...form, taxMode: v })}
          />
        </div>
      </Modal>
    </div>
  );
}
